import React from "react";

type Tone = "neutral" | "brand" | "accent" | "success" | "warning" | "danger";

interface PageHeaderBadge {
  label: string;
  tone?: Tone;
}

interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  badges?: PageHeaderBadge[];
  serifTitle?: boolean;
  compact?: boolean;
  className?: string;
}

interface MetricItem {
  label: string;
  value: React.ReactNode;
  detail?: string;
  tone?: Tone;
}

interface MetricStripProps {
  items: MetricItem[];
  className?: string;
}

interface FilterToolbarProps {
  children: React.ReactNode;
  sticky?: boolean;
  className?: string;
}

interface SegmentedItem {
  key: string;
  label: string;
  count?: React.ReactNode;
}

interface SegmentedViewSwitchProps {
  items: SegmentedItem[];
  activeKey: string;
  onChange: (key: string) => void;
  className?: string;
}

interface SectionCardProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

interface EmptyStateProps {
  title: string;
  message: string;
  action?: React.ReactNode;
}

const toneClassMap: Record<Tone, string> = {
  neutral: "tone-neutral",
  brand: "tone-brand",
  accent: "tone-accent",
  success: "tone-success",
  warning: "tone-warning",
  danger: "tone-danger",
};

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  badges = [],
  serifTitle = false,
  compact = false,
  className = "",
}: PageHeaderProps) {
  return (
    <section className={`page-header ${compact ? "is-compact" : ""} ${className}`.trim()}>
      <div className="page-header-copy">
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h1 className={`page-title ${serifTitle ? "page-title-serif" : ""}`.trim()}>{title}</h1>
        {description ? <p className="page-copy">{description}</p> : null}
        {badges.length > 0 ? (
          <div className="page-badges">
            {badges.map((badge) => (
              <span
                key={badge.label}
                className={`badge-pill ${toneClassMap[badge.tone || "neutral"]}`.trim()}
              >
                {badge.label}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      {actions ? <div className="page-header-actions">{actions}</div> : null}
    </section>
  );
}

export function MetricStrip({ items, className = "" }: MetricStripProps) {
  return (
    <section className={`metric-strip ${className}`.trim()}>
      {items.map((item) => (
        <article
          key={`${item.label}-${String(item.value)}`}
          className={`metric-card ${toneClassMap[item.tone || "neutral"]}`.trim()}
        >
          <div className="metric-label">{item.label}</div>
          <div className="metric-value">{item.value}</div>
          {item.detail ? <div className="metric-detail">{item.detail}</div> : null}
        </article>
      ))}
    </section>
  );
}

export function FilterToolbar({ children, sticky = false, className = "" }: FilterToolbarProps) {
  return (
    <section className={`filter-toolbar ${sticky ? "is-sticky" : ""} ${className}`.trim()}>
      {children}
    </section>
  );
}

export function SegmentedViewSwitch({
  items,
  activeKey,
  onChange,
  className = "",
}: SegmentedViewSwitchProps) {
  return (
    <div className={`segmented-switch ${className}`.trim()}>
      {items.map((item) => {
        const active = item.key === activeKey;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onChange(item.key)}
            className={`segmented-item ${active ? "is-active" : ""}`.trim()}
          >
            <span>{item.label}</span>
            {item.count != null ? <span className="segmented-count">{item.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function SectionCard({
  title,
  subtitle,
  actions,
  children,
  className = "",
}: SectionCardProps) {
  return (
    <section className={`section-card ${className}`.trim()}>
      {title || subtitle || actions ? (
        <div className="section-card-header">
          <div className="min-w-0">
            {title ? <h2 className="section-card-title">{title}</h2> : null}
            {subtitle ? <p className="section-card-subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="section-card-actions">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function DataTableShell(props: SectionCardProps) {
  return <SectionCard {...props} className={`data-table-shell ${props.className || ""}`.trim()} />;
}

export function StickyActionBar({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`sticky-action-bar ${className}`.trim()}>{children}</div>;
}

export function EmptyState({ title, message, action }: EmptyStateProps) {
  return (
    <div className="empty-state">
      <div>
        <div className="label-caps">{title}</div>
        <p className="mt-2">{message}</p>
        {action ? <div className="mt-4">{action}</div> : null}
      </div>
    </div>
  );
}
